import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from './config/axios';
import { WA_PRESET_COLORS } from './features/employees/styles/personalakteStyles';

/**
 * Interface for location object structure
 */
interface Location {
  id: number;
  name: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for qualification object structure
 */
interface Qualification {
  id: number;
  name: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for work area form data structure
 */
interface WorkAreaFormData {
  location: string;
  name: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  radius_meters: number;
  color: string;
}

/**
 * Interface for work area creation payload
 */
interface WorkAreaCreatePayload {
  location: number | null;
  name: string;
  address: string;
  radius_meters: number;
  color: string;
  employees: number[];
  required_qualifications: number[];
}

function WorkAreaCreate(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/organization?tab=work-areas';

  // Liste aller Locations (für das Dropdown)
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState<boolean>(false);

  // Qualifications
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [requiredQualifications, setRequiredQualifications] = useState<string[]>([]);

  const [formData, setFormData] = useState<WorkAreaFormData>({
    location: '',        // ID der gewählten Location
    name: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    radius_meters: 50,
    color: '#5271A5',
  });

  // Lade alle Locations => Dropdown
  useEffect(() => {
    axiosInstance
      .get<Location[] | { results: Location[] }>('/api/admin/locations/')
      .then((res) => {
        // Handle both array and paginated response formats
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setLocations(data);
      })
      .catch((err: any) => console.error('Error loading Locations:', err));
  }, []);

  // Lade alle Qualifications
  // useEffect(() => {
  //   axiosInstance
  //     .get<Qualification[]>('/api/qualifications/')
  //     .then((res) => setQualifications(res.data))
  //     .catch((err: any) => console.error('Error loading qualifications:', err));
  // }, []);

  // Form-Change-Handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'number' ? Number(value) : value 
    }));
  };

  const handleSave = (): void => {
    const {
      location,
      name,
      street,
      houseNumber,
      postalCode,
      city,
      radius_meters,
      color,
    } = formData;

    // Address-Feld: Kombiniere Street, HouseNumber, PLZ, City
    const combinedAddress = `${street} ${houseNumber}, ${postalCode} ${city}`.trim();

    const payload: WorkAreaCreatePayload = {
      location: location ? parseInt(location, 10) : null,
      name,
      address: combinedAddress,
      radius_meters,
      color,
      employees: selectedEmployees, // <-- IDs der zugeordneten Mitarbeiter
      required_qualifications: requiredQualifications.map(id => parseInt(id, 10)),
    };

    axiosInstance
      .post<any>('/api/admin/work-areas/', payload)
      .then(() => navigate(returnTo))
      .catch((err: any) => console.error('Error creating WorkArea:', err));
  };

  const handleQualificationAdd = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const val = e.target.value;
    if (val && !requiredQualifications.map(String).includes(String(val))) {
      setRequiredQualifications(prev => [...prev, String(val)]);
    }
  };

  const handleQualificationRemove = (qid: string): void => {
    setRequiredQualifications(prev => prev.filter(id => String(id) !== String(qid)));
  };

  const handleCancel = (): void => {
    navigate(returnTo);
  };

  return (
    <div data-testid="wac-root" style={{
      background: '#fff',
      borderRadius: 14,
      padding: '32px 36px 24px 36px',
      maxWidth: 540,
      margin: '40px auto',
      boxShadow: '0 8px 32px rgba(30,40,90,0.18)',
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: '1rem',
      position: 'relative'
    }}>
      <h2 data-testid="wac-title" style={{margin: 0, marginBottom: 18, fontWeight: 600, color: '#002b80', fontSize: '1.18rem'}}>New Work Area</h2>
      <div style={{marginBottom: 16}}>
        <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Location:</label>
        <select
          data-testid="wac-location-select"
          name="location"
          value={formData.location}
          onChange={handleChange}
          style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
        >
          <option value="">-- Choose Location --</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
      </div>
      <div style={{marginBottom: 16}} data-testid="wac-name-group">
        <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Name:</label>
        <input
          data-testid="wac-name-input"
          name="name"
          value={formData.name}
          onChange={handleChange}
          style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
        />
      </div>
      {/* Straße + Hausnummer nebeneinander */}
      <div style={{display:'flex', gap:12, marginBottom: 16}} data-testid="wac-street-group">
        <div style={{flex:2}}>
          <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Straße:</label>
          <input
            data-testid="wac-street-input"
            name="street"
            value={formData.street}
            onChange={handleChange}
            style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
          />
        </div>
        <div style={{flex:1}} data-testid="wac-house-group">
          <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Hausnummer:</label>
          <input
              data-testid="wac-house-input"
            name="houseNumber"
            value={formData.houseNumber}
            onChange={handleChange}
            style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
          />
        </div>
      </div>
      {/* PLZ + Ort nebeneinander */}
      <div style={{display:'flex', gap:12, marginBottom: 16}} data-testid="wac-postal-group">
        <div style={{flex:1}}>
          <label style={{fontWeight:500, marginBottom:4, display:'block'}}>PLZ:</label>
          <input data-testid="wac-postal-input"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
          />
        </div>
        <div style={{flex:2}} data-testid="wac-city-group">
          <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Ort:</label>
          <input data-testid="wac-city-input"
            name="city"
            value={formData.city}
            onChange={handleChange}
            style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
          />
        </div>
      </div>
      <div style={{marginBottom: 16}} data-testid="wac-radius-group">
        <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Radius (m):</label>
        <input
          data-testid="wac-radius-input"
          type="number"
          name="radius_meters"
          value={formData.radius_meters}
          onChange={handleChange}
          style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
        />
      </div>
      <div style={{marginBottom: 18}}>
        <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Color:</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {WA_PRESET_COLORS.map((c) => {
            const selected = formData.color.toLowerCase() === c.toLowerCase();
            return (
              <button
                key={c}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: c,
                  border: selected ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: selected ? `0 0 0 2px ${c}, 0 1px 4px rgba(0,0,0,0.18)` : 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={c}
              >
                {selected && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 7l3 3 5-5.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {/* Qualifications als Chips + Dropdown - Temporarily disabled until /api/qualifications/ endpoint is available */}
      {/* <div style={{marginBottom: 18}}>
        <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Qualifications:</label>
        <div style={{display:'flex', flexWrap:'wrap', gap:8, marginBottom:8}}>
          {requiredQualifications.map(qid => {
            const q = qualifications.find(q => String(q.id) === String(qid));
            return (
              <span key={qid} style={{
                display:'inline-flex', alignItems:'center', background:'#e0e0e0', color:'#222',
                borderRadius: 16, padding:'4px 12px', fontSize:'0.97rem'
              }}>
                {q ? q.name : qid}
                <span
                  style={{marginLeft:8, cursor:'pointer', fontWeight:700}}
                  onClick={() => handleQualificationRemove(qid)}
                  title="Entfernen"
                >×</span>
              </span>
            );
          })}
        </div>
        <select
          value=""
          onChange={handleQualificationAdd}
          style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
        >
          <option value="">+ Add Qualification</option>
          {qualifications
            .filter(q => !requiredQualifications.map(String).includes(String(q.id)))
            .map(q => (
              <option key={q.id} value={q.id}>{q.name}</option>
            ))}
        </select>
      </div> */}
      <div style={{display:'flex', justifyContent:'flex-end', gap:12, marginTop: 18}}>
        <button data-testid="wac-save-button"
          onClick={handleSave}
          style={{
            background:'#002b80', color:'#fff', border:'none', borderRadius:6, padding:'8px 22px', cursor:'pointer', fontWeight:600, fontSize:'1rem', boxShadow:'0 1px 2px rgba(30,40,90,0.07)', transition:'background 0.18s'}}
        >
          Save
        </button>
        <button data-testid="wac-cancel-button"
          onClick={handleCancel}
          style={{
            background:'#f5f5f5', color:'#03045e', border:'1px solid #e0e0e0', borderRadius:6, padding:'8px 20px', cursor:'pointer', fontWeight:500, fontSize:'1rem', transition:'background 0.18s'}}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default WorkAreaCreate; 